/**
 * An item seen from outside its space, on the same item page as inside, read-only. Public (`/p/<token>`): for
 * anyone with its link, signed in or not; with its reviews and its conversation when it shows them (and where it
 * takes comments, anyone signed in comments). A guest's (`/g/<item id>`): the item shared with them, where they
 * comment. Whoever can open it in its space goes straight there.
 */
import { useEffect, useMemo, useRef } from "preact/hooks";
import { api } from "../api/client.ts";
import type { ItemDetail, OutsideItem, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { useEntitiesLoader } from "../lib/entities.ts";
import { useLinkTarget } from "../lib/linkTarget.ts";
import { useLive } from "../lib/live.ts";
import type { Outside } from "../lib/paths.ts";
import { outsideRoom, usePresence } from "../lib/presence.ts";
import { type Resource, useResource } from "../lib/resource.ts";
import { me } from "../lib/state.ts";
import { browserZone } from "../lib/zone.ts";
import { Async, EmptyState, Loading } from "../ui/misc.tsx";
import { ItemView } from "./Item.tsx";

const NONE = { errors: 0, items: 0, notes: 0, triage: 0, warnings: 0 };
const NOT_FOUND = 404;
/** How often an open page loads again (its file links, signed for two minutes, stay good). */
const FRESH_MS = 90_000;

/** A stand-in for its space, as the item page needs one: read-only, its reads going to the link's own. */
function standIn(o: OutsideItem, outside: Outside): SpaceOut & { outside: Outside } {
  return {
    id: outside.api,
    org: "",
    org_name: "",
    name: o.space_name,
    time_zone: browserZone(),
    triage_email_address: "",
    schema_locked: true,
    rules_locked: true,
    role: "viewer",
    head: o.item.head,
    counts: { ...NONE, entities: 0, members: 0, queue: 0, rules: 0, held: 0, held_personal: 0, forwarding: 0 },
    entities: [],
    no_entity: NONE,
    kinds: [],
    // Nothing about its org's plan shows outside it.
    standing: { state: "active", usage: { people: 0, spaces: 0, storage: 0, emails_available: 0 } },
    outside,
  };
}

/** The item at `api` (`/public/<token>` or `/shared/<id>`) on its page, whose address is `home`. */
function OutsidePage({ api: base, home }: { api: string; home: string }) {
  const r = useResource(base, (signal) => api.get<OutsideItem>(base, undefined, signal));
  const { reload } = r;
  const again = useRef(reload);
  again.current = reload;
  // Live: anything of it changing loads it again; it going (unshared, deleted) too, and then it's gone from here.
  useLive(base, reload);
  // Among those looking at it: an invited guest as a person (seen, and seeing who's there), a public link's visitor
  // counted only (the organization's people see how many; nobody sees who).
  usePresence(outsideRoom(base));
  // Its file links last two minutes: loading it again now and then (while it's seen) signs fresh ones.
  useEffect(() => {
    const visible = () => document.visibilityState === "visible";
    const timer = setInterval(() => (visible() ? again.current() : undefined), FRESH_MS);
    const back = () => (visible() ? again.current() : undefined);
    document.addEventListener("visibilitychange", back);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", back);
    };
  }, []);
  // Not there anymore (or not for you): nothing of it stays on screen.
  if (r.error?.status === NOT_FOUND) {
    return <EmptyState title={t("link.missing")}>{t("link.missingHint")}</EmptyState>;
  }
  return (
    <Async r={r} whole="page">
      {(o) => <OutsideView o={o} base={base} home={home} reload={reload} />}
    </Async>
  );
}

/** Keeps the entities the item's page shows loaded, for icons anywhere on it (links to items in a text). */
function EntitiesLoader({ space }: { space: SpaceOut }) {
  useEntitiesLoader(space);
  return null;
}

function OutsideView({ o, base, home, reload }: { o: OutsideItem; base: string; home: string; reload: () => void }) {
  const space = useMemo(() => {
    const seen = Object.values(o.item.links)
      .flat()
      .filter((b) => b.outside);
    const links = Object.fromEntries(seen.map((b) => [b.id, b.outside ?? home]));
    const items = seen.map((b) => ({
      id: b.id,
      title: b.title,
      entity: b.entity ?? null,
      kind: b.file ? ("file" as const) : ("record" as const),
      file: b.file ?? null,
    }));
    return standIn(o, { api: base, home, shows: o.shows, links, items });
  }, [o, base, home]);
  // Read-only: nothing replaces it but loading it again.
  const item: Resource<ItemDetail> = { data: o.item, error: undefined, reload, set: reload };
  return (
    <>
      <EntitiesLoader space={space} />
      <ItemView space={space} r={item} item={o.item} />
    </>
  );
}

/**
 * A public link: whoever can open it in its space goes there, with everything (the link keeps working for them
 * after it stops being public); anyone else gets its public page.
 */
export function PublicItem({ token }: { token: string }) {
  const { stay } = useLinkTarget(me.value ? `/links/shared/${token}` : null);
  return stay ? <OutsidePage api={`/public/${token}`} home={`/p/${token}`} /> : <Loading />;
}

/**
 * An item shared with you, its guest (`/g/<item id>`). Once you can open it in its space (you joined it), the link
 * leads there.
 */
export function SharedItem({ id }: { id: string }) {
  const { stay } = useLinkTarget(`/links/${id}`, (at) => at.guest);
  return stay ? <OutsidePage api={`/shared/${id}`} home={`/g/${id}`} /> : <Loading />;
}
